import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useDocumentMeta({
    title: keyword ? `搜索“${keyword}” - 黑丝时尚搭配与潮流资讯` : "搜索 - 黑丝时尚搭配与潮流资讯",
    description: keyword
      ? `与“${keyword}”相关的黑丝搭配指南、丝袜穿搭技巧与潮流资讯搜索结果。`
      : "通过关键词搜索黑丝的搭配灵感、穿搭技巧与潮流资讯。",
    keywords: `黑丝搜索,${keyword},丝袜搭配,时尚穿搭`,
  });

  const results = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return [];
    return articles
      .map((item) => {
        let score = 0;
        if (item.title.toLowerCase().includes(q)) score += 6;
        if (item.excerpt.toLowerCase().includes(q)) score += 3;
        item.tags.forEach((tag) => {
          if (tag.toLowerCase().includes(q)) score += 4;
        });
        if (item.title.toLowerCase().startsWith(q)) score += 2;
        return { item, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity)
      .map((entry) => entry.item);
  }, [keyword]);

  const recommendations = useMemo(
    () => [...articles].sort((a, b) => b.popularity - a.popularity).slice(0, 3),
    []
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    if (value) setSearchParams({ q: value });
  };

  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-24 md:px-8 md:pt-28">
      <Breadcrumb items={[{ label: "搜索结果" }]} />

      <header className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">Search</p>
        <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">全站搜索</h1>
        <form onSubmit={submit} className="relative mt-6 max-w-2xl">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400">
            <i className="ri-search-line" />
          </span>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入关键词，例如：街拍、职场、晚宴、保养"
            className="h-14 w-full rounded-full border border-foreground-200 bg-background-50 pl-12 pr-32 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400 md:text-base"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-foreground-950 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            搜索
          </button>
        </form>
      </header>

      {keyword ? (
        <>
          <p className="mt-6 text-sm text-foreground-600">
            关键词 <strong className="text-foreground-950">“{keyword}”</strong> 共找到{" "}
            <strong className="text-primary-600">{results.length}</strong> 条结果，已按相关度排序
          </p>

          {results.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-4">
              {results.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group grid grid-cols-1 items-center gap-4 rounded-lg border border-foreground-200/70 bg-background-50 p-3 sm:grid-cols-[160px_1fr] md:p-4"
                >
                  <div className="aspect-[4/3] w-full overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-40">
                    <img
                      src={item.cover}
                      alt={`${item.title} 搜索结果`}
                      title={`${item.title} - 黑丝时尚搭配`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                      {/* Highlighted title */}
                      <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-secondary-900">
                        {item.tags[0]}
                      </span>
                      <span>{item.date}</span>
                    </div>
                    <h3 className="mt-2 line-clamp-2 font-heading text-lg font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-sm text-foreground-600">{item.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-foreground-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
                <i className="ri-search-eye-line" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-bold text-foreground-950">没有找到“{keyword}”相关内容</h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                尝试更换关键词，或浏览下方的热门推荐内容。
              </p>
              <div className="mt-8 w-full max-w-3xl text-left">
                <h4 className="mb-4 font-heading text-lg font-bold text-foreground-950">为你推荐</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {recommendations.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="group overflow-hidden rounded-lg border border-foreground-200/70 bg-background-50"
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-background-200">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 黑丝时尚搭配`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-3">
                        <h5 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </h5>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mt-10 rounded-lg border border-foreground-200/70 bg-background-100 p-8">
          <h3 className="font-heading text-lg font-bold text-foreground-950">试试这些热门关键词</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {["街拍", "职场", "晚宴", "保养", "叠穿", "运动鞋", "趋势", "显瘦"].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setSearchParams({ q: tag })}
                className="rounded-full bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-secondary-100 cursor-pointer"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}